import { useEffect, useState, useMemo } from 'react';
import { ShippingSKU } from '@lambda/apis/src/shipping/types';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import { VendorDetails } from '@lambda/apis/src/dynamoDb/types';
import { VendorDetailsT } from './types';
import { logger } from '@/lib/logger';

type UseVendorDetailsParams = {
  vendorName: string | null;
  categoryPk: string;
  price: number;
};

const useVendorDetails = ({
  vendorName,
  categoryPk,
  price,
}: UseVendorDetailsParams) => {
  const [expressShipping, setExpressShipping] = useState<ShippingSKU | null>(
    null,
  );
  const [goodReviewsPercentage, setGoodReviewsPercentage] = useState<
    number | null
  >(null);
  const [reviewsCount, setReviewsCount] = useState<number | null>(null);
  const [ordersCount, setOrdersCount] = useState<number | null>(null);
  const [company, setCompany] = useState<VendorDetails['company'] | null>(null);

  const warrantyKey = price === 0 ? null : `${categoryPk}|${price}`;
  const [warrantyResult, setWarrantyResult] = useState<{
    key: string;
    data: WarrantyT | null;
  } | null>(null);

  const isCurrentWarranty = warrantyResult?.key === warrantyKey;
  const warranty = isCurrentWarranty ? warrantyResult?.data ?? null : null;
  const warrantyLoading = warrantyKey != null && !isCurrentWarranty;

  // Fetch Warranty
  useEffect(() => {
    if (warrantyKey == null) {
      return undefined;
    }
    const controller = new AbortController();

    const fetchWarranty = async () => {
      try {
        const data = await fetch(
          `/api/warranty/search-warranty-sku?price=${
            price / 100
          }&categoryPk=${categoryPk}`,
          { signal: controller.signal },
        ).then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch warranty');
          }

          return res.json() as Promise<WarrantyT | null>;
        });

        setWarrantyResult({ key: warrantyKey, data });
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        logger.error({ err, vendorName }, 'Warranty fetch failed');
        // No plan for this offer rather than the previous offer's.
        setWarrantyResult({ key: warrantyKey, data: null });
      }
    };

    fetchWarranty();

    return () => controller.abort();
  }, [warrantyKey]);

  // Fetch Express Shipping
  useEffect(() => {
    setExpressShipping(null);

    if (!vendorName) {
      return undefined;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({ vendor: vendorName, categoryPk });

    async function fetchShipping() {
      try {
        const res = await fetch(`/api/express-shipping/offer?${params}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          return;
        }

        const json = await res.json();

        setExpressShipping(json?.shippingOffer ?? null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          logger.error({ e, vendorName }, 'Shipping fetch failed');
        }
      }
    }

    fetchShipping();

    return () => controller.abort();
  }, [vendorName, categoryPk]);

  // Fetch Vendor Reviews Aggregate
  useEffect(() => {
    setGoodReviewsPercentage(null);
    setReviewsCount(null);

    if (!vendorName) {
      return undefined;
    }

    const controller = new AbortController();

    async function fetchReviewsAggregate() {
      try {
        const res = await fetch(
          `/api/v2/reviews/aggregate?vendor=${encodeURIComponent(
            vendorName as string,
          )}`,
          { signal: controller.signal },
        );

        if (!res.ok) {
          return;
        }

        const json = (await res.json()) as {
          total?: number;
          goodReviewsPercent?: number;
        } | null;

        setReviewsCount(json?.total ?? null);
        setGoodReviewsPercentage(json?.goodReviewsPercent ?? null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          logger.error({ e, vendorName }, 'Reviews aggregate fetch failed');
        }
      }
    }

    fetchReviewsAggregate();

    return () => controller.abort();
  }, [vendorName]);

  // Fetch Vendor Company Details
  useEffect(() => {
    if (!vendorName) {
      return undefined;
    }

    const controller = new AbortController();

    async function fetchVendorCompany() {
      try {
        const res = await fetch(
          `/api/products/vendor-details?vendor=${encodeURIComponent(
            vendorName as string,
          )}`,
          { signal: controller.signal },
        );

        if (!res.ok) {
          return;
        }

        const json = (await res.json()) as VendorDetails;

        setCompany(json.company ?? null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          logger.error({ e, vendorName }, 'Vendor Company fetch failed');
        }
      }
    }

    fetchVendorCompany();

    return () => controller.abort();
  }, [vendorName]);

  // Fetch Orders Count
  useEffect(() => {
    if (!vendorName) {
      return undefined;
    }

    const controller = new AbortController();

    async function fetchOrdersCount() {
      try {
        const res = await fetch(
          `/api/products/vendor-product-sold?vendor=${encodeURIComponent(
            vendorName as string,
          )}`,
          { signal: controller.signal },
        );

        if (!res.ok) {
          return;
        }

        const json = await res.json();

        setOrdersCount(json ?? null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          logger.error({ e, vendorName }, 'Orders fetch failed');
        }
      }
    }

    fetchOrdersCount();

    return () => controller.abort();
  }, [vendorName]);

  // Combine results into one object
  const vendorDetails: VendorDetailsT = useMemo(
    () => ({
      name: vendorName,
      expressShipping,
      goodReviewsPercentage,
      reviewsCount,
      ordersCount,
      warranty: { loading: warrantyLoading, data: warranty },
      company,
    }),
    [
      vendorName,
      expressShipping,
      goodReviewsPercentage,
      reviewsCount,
      ordersCount,
      warranty,
      warrantyLoading,
      company,
    ],
  );

  return vendorDetails;
};

export default useVendorDetails;
